import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { t } from 'i18next';
import {
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage,
  North,
  South
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import BakeasyLinkButton from '../buttons/BakeasyLinkButton';
import { GRAY_200, GRAY_800, GREEN_50, RED_50 } from '../../constants/Colors';
import { getCurrency } from '../../utils/commonUtils';

export interface HeadCell {
  disablePadding?: boolean;
  id: string;
  label: string;
  numeric?: boolean | false;
  isChip?: boolean | false;
  sort?: boolean | false;
  align?: string | 'left';
  width?: string;
}

interface BakeasyTableMainProps {
  headers?: any[];
  headCells?: any[];
  tableData: any;
  rowProps?: number;
  pageNo?: number;
  navigate?: any;
  count?: any;
  refreshPage?: any;
  refreshPageData?: any;
  api?: string;
  sortBy?: any;
  sortDirection?: any;
  search?: any;
  filter?: any;
}

const BakeasyTable = React.forwardRef(
  (
    props: BakeasyTableMainProps,
    ref: React.ForwardedRef<{
      handleChangePage: (event: unknown, newPage: number) => void;
    }>
  ) => {
    const { headers, tableData, count, refreshPageData, pageNo, search, filter } = props;
    const [order, setOrder] = React.useState<number>(filter?.sortDirection ?? -1);
    const [orderBy, setOrderBy] = React.useState<string>(filter?.sortBy ?? '');
    const [mouseOver, setMouseOver] = React.useState<string>('');
    const [tableDataList, setTableDataList] = React.useState<any>([]);
    const [page, setPage] = React.useState(pageNo ?? 0);

    React.useImperativeHandle(ref, () => ({
      handleChangePage
    }));

    const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
      refreshPageData(newPage, search, filter);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      let newFilter: any = {
        ...filter,
        limit: Number(event.target.value)
      };
      setPage(0);
      refreshPageData(0, search, newFilter);
    };

    React.useEffect(() => {
      setTableDataList(tableData);
    }, [tableData]);

    const ArrowIcon = (property: any) => {
      let northColor = 'gray',
        southColor = 'gray';

      if (orderBy === property) {
        if (order === -1) {
          northColor = 'black';
          southColor = 'gray';
        } else if (order === 1) {
          northColor = 'gray';
          southColor = 'black';
        }
      }

      return (
        <Grid
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'Center',
            alignItems: 'Center',
            margin: 0
          }}
        >
          <IconButton size="small" sx={{ color: orderBy === property ? 'black' : 'gray' }}>
            {orderBy === property && order === 1 && <North fontSize="inherit" />}
            {orderBy === property && order === -1 && <South fontSize="inherit" />}
            {mouseOver === property && mouseOver !== orderBy && <North fontSize="inherit" />}
          </IconButton>
        </Grid>
      );
      //   return (
      //     <Grid
      //       style={{
      //         display: 'flex',
      //         flexDirection: 'row',
      //         justifyContent: 'Center',
      //         alignItems: 'Center',
      //         //marginLeft: "4px",
      //         margin: 0
      //       }}
      //     >
      //       <IconButton
      //         size="small"
      //         sx={{ color: northColor }}
      //         onClick={event => {
      //           setOrder(-1);
      //           setOrderBy(property);
      //           setPage(0);
      //           let newFilter: any = {
      //             ...filter,
      //             sortBy: property,
      //             sortDirection: -1
      //           };
      //           refreshPageData(0, search, newFilter);
      //         }}
      //       >
      //         <North fontSize="inherit" />
      //       </IconButton>
      //       <IconButton
      //         size="small"
      //         sx={{ marginLeft: -1.5, color: southColor }}
      //         onClick={event => {
      //           setOrder(1);
      //           setOrderBy(property);
      //           setPage(0);
      //           let newFilter: any = {
      //             ...filter,
      //             sortBy: property,
      //             sortDirection: 1
      //           };
      //           refreshPageData(0, search, newFilter);
      //         }}
      //       >
      //         <South fontSize="inherit" />
      //       </IconButton>
      //     </Grid>
      //   );
    };

    function CustomTableCell(id: any, row: any) {
      switch (id) {
        case 'name':
          return <BakeasyLinkButton label={row[id]} onClick={row['onNameClick']} />;
        case 'productName':
          return <BakeasyLinkButton label={row[id]} onClick={row['onProductNameClick']} />;
        case 'storeName':
          return <BakeasyLinkButton label={row[id]} onClick={row['onStoreNameClick']} />;
        case 'supplierName':
          return <BakeasyLinkButton label={row[id]} onClick={row['onSupplierNameClick']} />;
        case 'contact':
          return <BakeasyLinkButton label={row[id]} onClick={row['onContactClick']} />;
        case 'messageDateTime':
          const dateTime = row[id];
          const date = dateTime.substring(0, 10);
          const time = dateTime.substring(11);
          return (
            <div>
              <div> {date}</div>
              <div style={{ color: GRAY_800, fontSize: '0.8rem' }}>{time}</div>
            </div>
          );
        case 'subject':
          return (
            <div
              style={{
                //whiteSpace: 'normal',
                wordWrap: 'break-word'
                //overflow: 'hidden' // Optional: Hide overflowing text
              }}
            >
              {row[id]}
            </div>
          );
        default:
          // return row[id];
          return (
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row[id]}
            </div>
          );
      }
    }

    function getHeaderLabel(header: any) {
      switch (header?.id) {
        case 'storeCost':
        case 'ordervalue':
        case 'totalPrice':
        case 'price':
        case 'totalOrderValue':
          return t(header?.label) + getCurrency('USA');
        default:
          return t(header?.label);
      }
    }

    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Paper sx={{ width: '100%' }}>
          <TableContainer style={{ overflowX: 'auto' }}>
            <Table stickyHeader sx={{ minWidth: 500 }} aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headers?.map((header, index) => (
                    <TableCell
                      style={{
                        padding: 10,
                        // paddingRight: 5,
                        // paddingLeft: 5,
                        // paddingTop: 10,
                        // paddingBottom: 10,
                        backgroundColor: GRAY_200,
                        textAlign: header.align,
                        whiteSpace: 'nowrap',
                        // overflow: 'hidden',
                        // textOverflow: 'ellipsis',
                        width: header?.width ?? 'auto',
                        maxWidth: header?.width ?? 'auto',
                        minWidth: header?.width ?? 'auto'
                      }}
                      key={index}
                    >
                      {!header.sort ? (
                        <Typography style={{ fontWeight: 'bold' }}>
                          {getHeaderLabel(header)}
                        </Typography>
                      ) : (
                        <TableSortLabel
                          IconComponent={() => ArrowIcon(header?.id)}
                          onMouseOver={(event: any) => {
                            setMouseOver(header?.id);
                          }}
                          onMouseOut={(event: any) => {
                            setMouseOver('');
                          }}
                          onClick={(event: any) => {
                            let _order = order === 1 && orderBy === header?.id ? -1 : 1;
                            let _orderBy =
                              order === -1 && orderBy === header?.id ? 'createdDate' : header?.id;

                            if (_orderBy === 'createdDate' && orderBy === header?.id) {
                              _order = order === 1 ? -1 : 1;
                            }

                            let newFilter: any = {
                              ...filter,
                              sortBy: _orderBy,
                              sortDirection: _order
                            };
                            setOrder(_order);
                            setOrderBy(_orderBy);
                            setPage(0);
                            refreshPageData(0, search, newFilter);
                          }}
                        >
                          <Typography style={{ fontWeight: 'bold' }}>
                            {getHeaderLabel(header)}
                          </Typography>
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableDataList?.map((row: any, index: any) => (
                  <TableRow
                    key={index}
                    // sx={{
                    //   backgroundColor: row.status === 'new' ? GREEN_50 : 'auto'
                    // }}
                    sx={{
                      backgroundColor: (() => {
                        if (row?.priority?.props?.label === 'urgent' && row?.status === 'new') {
                          return RED_50;
                        } else if (row?.status === 'new') {
                          return GREEN_50;
                        } else {
                          return 'auto';
                        }
                      })()
                    }}
                  >
                    {headers?.map((header: any, headerIndex: any) => (
                      <TableCell
                        key={headerIndex}
                        style={{
                          padding: 10,
                          textAlign: header.align,
                          width: header?.width ?? 'auto',
                          maxWidth: header?.width ?? 'auto',
                          minWidth: header?.width ?? 'auto',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          paddingRight: header?.align === 'right' ? 20 : 0
                        }}
                        align={header.align}
                      >
                        {CustomTableCell(header.id, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={count}
            rowsPerPage={parseFloat(filter?.limit)}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            labelRowsPerPage={t('ROWS_PER_PAGE')}
          />
        </Paper>
      </Box>
    );
  }
);

export default BakeasyTable;

export interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}
